import { useEffect, useState } from 'react';
import { CONFIG } from '../../../config';

export type ZipLookup =
  | { status: 'idle' | 'loading' | 'not-found' | 'error'; zip?: string }
  | { status: 'found'; zip: string; city: string; state: string };

type Resolved = Exclude<ZipLookup, { status: 'idle' | 'loading' }>;

type SmartyZipcode = { default_city: string; state_abbreviation: string };
type SmartyResponse = [{ zipcodes?: SmartyZipcode[] }];

const cache = new Map<string, Resolved>();

const lookUp = async (zip: string): Promise<Resolved> => {
  const cached = cache.get(zip);
  if (cached) return cached;

  try {
    const response = await fetch(`${CONFIG.DISCOVERY_API_URL}/lookup?zipcode=${zip}`, {
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) return { status: 'error', zip };

    const [result]: SmartyResponse = await response.json();
    const zipcode = result?.zipcodes?.[0];
    const resolved: Resolved = zipcode
      ? { status: 'found', zip, city: zipcode.default_city, state: zipcode.state_abbreviation }
      : { status: 'not-found', zip };

    cache.set(zip, resolved);
    return resolved;
  } catch {
    return { status: 'error', zip };
  }
};

export const useZipLookup = (zip: string | undefined): ZipLookup => {
  const [lookup, setLookup] = useState<ZipLookup>({ status: 'idle' });

  useEffect(() => {
    if (!zip) {
      setLookup({ status: 'idle' });
      return;
    }

    let current = true;
    setLookup(cache.get(zip) ?? { status: 'loading', zip });
    lookUp(zip).then(resolved => current && setLookup(resolved));

    return () => {
      current = false;
    };
  }, [zip]);

  return lookup.zip === zip ? lookup : { status: zip ? 'loading' : 'idle', zip };
};
